import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Chip, LinearProgress, TextField } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tone } from '../../../utils/uiTokens'
import { BFF_SERVICE } from '../../../utils/constants'
import storage from '../../../utils/storageService'
import { salaryApiSlice } from '../../../features/salary/salarySlice'
import SalaryDetailView from './components/SalaryDetailView'
import { Content, Header, Wrapper } from './styles'
import ApprovalBadge from './components/ApprovalBadge'
import { useHeadScope } from '../../head/headScope'

const WEEKS = [1, 2, 3, 4]

const MentorSalaryDetailPage: React.FC = () => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { mentorUserId } = useParams()
    const [params] = useSearchParams()

    const courseSubjectId = Number(params.get('courseSubjectId') || 0)
    const cycleNumber = Number(params.get('cycleNumber') || 1)
    const mentorId = Number(mentorUserId)
    // пән басшы: только чтение, плюс недельный срез (без денег)
    const { readOnly } = useHeadScope()
    const [weekNumber, setWeekNumber] = useState(0)
    const isWeek = weekNumber !== 0

    const { data, isFetching } = salaryApiSlice.useGetMentorSalaryDetailQuery({
        mentorUserId: mentorId,
        courseSubjectId,
        cycleNumber,
        weekNumber,
    })
    const { data: audit } = salaryApiSlice.useGetSalaryAuditQuery({ cycleNumber, mentorUserId: mentorId })

    const [saveSurvey, { isLoading: savingSurvey }] = salaryApiSlice.useSaveSurveyMutation()
    const [saveManualFactor] = salaryApiSlice.useSaveManualFactorMutation()
    const [closeCycle, { isLoading: closing }] = salaryApiSlice.useCloseCycleMutation()

    const [surveyInput, setSurveyInput] = useState<string>('')
    const surveyValue = surveyInput !== '' ? surveyInput : String(data?.surveyPercent ?? 0)
    const isClosed = data?.cycle?.status === 'CLOSED'
    // опрос можно поправить и в закрытом месяце; бекітілген пән басшы - нет
    const surveyLocked = data?.approvalStatus === 'APPROVED'
    const mentorName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim() || `#${mentorId}`

    const onSaveSurvey = async () => {
        try {
            await saveSurvey({ mentorUserId: mentorId, courseSubjectId, cycleNumber, percent: clampPct(Number(surveyValue)) }).unwrap()
            setSurveyInput('')
            toast.success(formatMessage({ id: 'salarySavedToast' }))
        } catch { /* общий обработчик */ }
    }

    const onSaveManual = async (enrollId: number, factor: string, percent: number) => {
        try {
            await saveManualFactor({ userSubjectEnrollId: enrollId, cycleNumber, factor, percent }).unwrap()
            toast.success(formatMessage({ id: 'salarySavedToast' }))
        } catch { /* общий обработчик */ }
    }

    const onClose = async () => {
        try {
            await closeCycle({ cycleNumber, courseSubjectId }).unwrap()
            toast.success(formatMessage({ id: 'salaryClosedToast' }))
        } catch { /* общий обработчик */ }
    }

    const onExport = async () => {
        try {
            const token = storage?.get('token')
            const res = await fetch(
                `${BFF_SERVICE}/mentor-salary/export?cycleNumber=${cycleNumber}&courseSubjectId=${courseSubjectId}`,
                { headers: token ? { authorization: `Bearer ${token}` } : {} },
            )
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `mentor-salary-${cycleNumber}.csv`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            toast.error(formatMessage({ id: 'salaryExportFail' }))
        }
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Box>
                        <Button onClick={() => navigate(readOnly ? '/head/salary' : '/mentor-salary')} sx={{ color: ui.sub, paddingLeft: 0 }}>
                            ← {formatMessage({ id: 'salaryBack' })}
                        </Button>
                        <H3>{mentorName}</H3>
                        <Box sx={{ color: ui.sub, fontSize: '13.5px', marginTop: '4px' }}>
                            {(data?.subjectTitleKz || data?.subjectTitleRu || '')} · {cycleNumber} {formatMessage({ id: 'month' })} · {data?.studentCount ?? 0} {formatMessage({ id: 'colStudents' })}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <ApprovalBadge status={data?.approvalStatus} comment={data?.approvalComment} />
                        <Chip
                            label={formatMessage({ id: isClosed ? 'salaryStatusClosed' : 'salaryStatusOpen' })}
                            sx={{ fontWeight: 700, backgroundColor: isClosed ? ui.track : tone.ok.bg, color: isClosed ? ui.sub : tone.ok.text }}
                        />
                        {!readOnly && (
                            <Button variant="outlined" onClick={onExport} sx={{ borderColor: ui.border, color: ui.ink }}>
                                {formatMessage({ id: 'salaryExport' })}
                            </Button>
                        )}
                        {!isClosed && !readOnly && (
                            <Button variant="contained" onClick={onClose} disabled={closing} sx={{ backgroundColor: ui.pink }}>
                                {formatMessage({ id: 'salaryCloseSubject' })}
                            </Button>
                        )}
                    </Box>
                </Header>

                {isFetching || !data ? (
                    <LinearProgress />
                ) : (
                    <>
                        <Box sx={surveyBox}>
                            <Box>
                                <Box sx={{ fontWeight: 600, color: ui.ink }}>{formatMessage({ id: 'salarySurveyLabel' })}</Box>
                                <Box sx={{ fontSize: '12.5px', color: ui.sub, marginTop: '4px' }}>{formatMessage({ id: 'salarySurveyHint' })}</Box>
                                {isClosed && !surveyLocked && !readOnly && (
                                    <Box sx={{ fontSize: '12.5px', color: tone.warn.text, marginTop: '4px' }}>{formatMessage({ id: 'salarySurveyClosedHint' })}</Box>
                                )}
                            </Box>
                            {readOnly ? (
                                <Box sx={{ fontWeight: 800, fontSize: '18px', color: ui.pink }}>{data.surveyPercent}%</Box>
                            ) : (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={surveyValue}
                                    disabled={surveyLocked}
                                    onChange={(e) => setSurveyInput(e.target.value)}
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{ width: 110 }}
                                />
                                <Box sx={{ color: ui.sub }}>%</Box>
                                <Button variant="contained" onClick={onSaveSurvey} disabled={surveyLocked || savingSurvey} sx={{ backgroundColor: ui.pink }}>
                                    {formatMessage({ id: 'salarySave' })}
                                </Button>
                            </Box>
                            )}
                        </Box>

                        {readOnly && (
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', marginTop: '14px' }}>
                                <Box sx={weekTab(!isWeek)} onClick={() => setWeekNumber(0)}>
                                    {formatMessage({ id: 'allWeeks' })}
                                </Box>
                                {WEEKS.map((w) => (
                                    <Box key={w} sx={weekTab(weekNumber === w)} onClick={() => setWeekNumber(w)}>
                                        {w} {formatMessage({ id: 'week' })}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {isWeek && (
                            <Box sx={{ fontSize: '12.5px', color: ui.muted, marginTop: '8px' }}>
                                {formatMessage({ id: 'salaryWeekNote' })}
                            </Box>
                        )}

                        <Box sx={{ marginTop: '18px' }}>
                            <SalaryDetailView
                                detail={data}
                                editable={!readOnly}
                                weekMode={isWeek}
                                weekNumber={weekNumber}
                                onSaveManual={onSaveManual}
                                initialGroup={params.get('group') ?? ''}
                            />
                        </Box>

                        <Box sx={{ color: ui.muted, fontSize: '12px', marginTop: '10px' }}>
                            {formatMessage({ id: 'salaryDetailFooter' })}
                        </Box>

                        {audit?.entries?.length ? (
                            <Box sx={{ marginTop: '26px' }}>
                                <H3>{formatMessage({ id: 'salaryAuditTitle' })}</H3>
                                <Box sx={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {audit.entries.map((a) => (
                                        <Box key={a.id} sx={auditRow}>
                                            <Box sx={{ color: ui.ink, fontSize: '13px' }}>{a.detail}</Box>
                                            <Box sx={{ color: ui.muted, fontSize: '12px' }}>{a.actor} · {formatDate(a.createdAt)}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : null}
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

const clampPct = (n: number) => Math.max(0, Math.min(100, Math.round(Number.isFinite(n) ? n : 0)))

const formatDate = (iso: string) => {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('ru-RU')
}

const weekTab = (active: boolean) => ({
    padding: '8px 14px',
    borderRadius: ui.radius,
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '13px',
    lineHeight: 1,
    fontWeight: active ? 700 : 500,
    color: active ? ui.pink : ui.sub,
    backgroundColor: active ? ui.pinkSoft : 'transparent',
    border: `1px solid ${active ? 'transparent' : ui.border}`,
})

const surveyBox = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    border: `1px solid ${ui.border}`,
    borderRadius: ui.radius,
    padding: '16px 18px',
    backgroundColor: ui.surface,
}

const auditRow = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    padding: '10px 14px',
    border: `1px solid ${ui.border}`,
    borderRadius: '10px',
    flexWrap: 'wrap',
}

export default MentorSalaryDetailPage
